import { Link } from "react-router-dom";
import NeutralHeader from "@/components/NeutralHeader";
import MinimalFooter from "@/components/MinimalFooter";
import { useSeo } from "@/lib/seo";
import { User, PieChart, ListChecks, Gauge, Compass, ArrowRight, Clock, BookOpen, ShieldCheck, FileText } from "lucide-react";

const COUPLE_IMG =
  "https://images.unsplash.com/photo-1508963493744-76fce69379c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const STEPS = [
  { icon: User, title: "Personal Information", copy: "Tell us a bit about yourself, including your age, household situation, and general financial background." },
  { icon: PieChart, title: "Financial Snapshot", copy: "Share key details about your savings, investments, income, expenses, and any existing debt." },
  { icon: ListChecks, title: "Planning Priorities", copy: "Let us know what matters most to you, such as retirement, healthcare, leaving a legacy, or other goals." },
  { icon: Gauge, title: "Get Your Score", copy: "Receive your personalized financial readiness score, with a clear overview of your strengths and opportunities." },
  { icon: Compass, title: "Next Steps", copy: "See helpful guidance and resources based on your results, so you can explore areas that may deserve a closer look." },
];

const EXPECT = [
  { icon: Clock, title: "Takes about 3–5 minutes", copy: "The assessment is quick and easy to complete, with no complicated forms." },
  { icon: BookOpen, title: "Educational, not financial advice", copy: "Your results are for informational purposes only and are designed to help you think through important planning questions." },
  { icon: ShieldCheck, title: "No obligation", copy: "There's no cost and no commitment. You can explore your results at your own pace." },
  { icon: FileText, title: "Designed to organize planning questions", copy: "Your results help identify areas that appear well organized and areas that may deserve a closer look." },
];

export default function HowItWorks() {
  useSeo({
    title: "How It Works | Financial Future Readiness Assessment",
    description:
      "A simple, 5-part Financial Future Readiness Assessment: Personal Information, Financial Snapshot, Planning Priorities, Get Your Score, and Next Steps. Takes about 3–5 minutes.",
    canonicalPath: "/how-it-works",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <NeutralHeader />
      <main id="main-content" tabIndex={-1} className="flex-1" data-testid="how-it-works-main">

        {/* HERO — atmospheric scrim with couple image on the right */}
        <section className="atmo-mountain">
          <div className="summit-container py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="section-pill mb-5" data-testid="hiw-eyebrow">How It Works</div>
              <h1
                className="font-serif"
                style={{ color: "#1E1968", fontSize: "clamp(2.2rem, 4.5vw, 3.5rem)", fontWeight: 600, lineHeight: 1.06, letterSpacing: "-0.015em" }}
                data-testid="hiw-title"
              >
                A Simple, 5-Part Assessment
              </h1>
              <p className="mt-5 text-lg" style={{ color: "#17233D", maxWidth: 560, lineHeight: 1.55 }}>
                Get a clearer picture of your financial future in just a few minutes.
              </p>
              <p className="mt-4 text-base" style={{ color: "#3a4d70", maxWidth: 560, lineHeight: 1.65 }}>
                Our assessment walks you through five simple steps, covering key areas of your financial life. You'll receive
                a personalized readiness score and insights to help you see what's on track and what may deserve a closer look.
              </p>
            </div>
            <div className="lg:col-span-6">
              <div
                style={{
                  background: "#ffffff",
                  padding: 10,
                  boxShadow: "0 10px 28px rgba(23, 35, 61, 0.08)",
                }}
              >
                <img
                  src={COUPLE_IMG}
                  alt=""
                  loading="eager"
                  className="w-full h-[280px] md:h-[400px] object-cover"
                  style={{ display: "block" }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* FIVE STEPS */}
        <section style={{ background: "#ffffff" }}>
          <div className="summit-container py-16 lg:py-20">
            <ol
              className="hidden md:grid relative"
              style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "1.5rem", listStyle: "none", padding: 0, margin: 0 }}
              data-testid="hiw-steps-desktop"
            >
              <div aria-hidden="true" style={{ position: "absolute", top: 68, left: "10%", right: "10%", height: 1, background: "#c7d9ec", zIndex: 0 }} />
              {STEPS.map(({ icon: Icon, title, copy }, i) => (
                <li key={title} className="relative flex flex-col items-center text-center" style={{ zIndex: 1 }} data-testid={`hiw-step-${i + 1}`}>
                  <div className="font-serif" style={{ color: "#258FC4", fontSize: "0.95rem", fontWeight: 600, letterSpacing: "0.04em", marginBottom: 10 }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 72, height: 72, display: "inline-flex", alignItems: "center", justifyContent: "center",
                      background: "#eaf2fa", color: "#1E1968", borderRadius: 999, border: "1px solid #cfe0f1",
                    }}
                  >
                    <Icon style={{ width: 30, height: 30 }} strokeWidth={1.4} />
                  </span>
                  <div className="font-serif mt-5" style={{ color: "#1E1968", fontSize: "1.15rem", fontWeight: 600, lineHeight: 1.25, maxWidth: 180 }}>
                    {title}
                  </div>
                  <p className="mt-3 text-sm" style={{ color: "#3a4d70", lineHeight: 1.6, maxWidth: 220 }}>{copy}</p>
                </li>
              ))}
            </ol>

            {/* Mobile */}
            <ol className="md:hidden relative" style={{ listStyle: "none", margin: 0, padding: "0 0 0 72px" }} data-testid="hiw-steps-mobile">
              <div aria-hidden="true" style={{ position: "absolute", top: 24, bottom: 24, left: 23, width: 1, background: "#c7d9ec" }} />
              {STEPS.map(({ icon: Icon, title, copy }, i) => (
                <li key={title} className="relative" style={{ marginBottom: i === STEPS.length - 1 ? 0 : "2rem" }}>
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute", left: -72, top: 0,
                      width: 48, height: 48,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      background: "#eaf2fa", color: "#1E1968", borderRadius: 999, border: "1px solid #cfe0f1",
                    }}
                  >
                    <Icon style={{ width: 22, height: 22 }} strokeWidth={1.5} />
                  </span>
                  <div className="font-serif" style={{ color: "#258FC4", fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.04em" }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.25, marginTop: 2 }}>
                    {title}
                  </div>
                  <p className="mt-2 text-sm" style={{ color: "#3a4d70", lineHeight: 1.6 }}>{copy}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* WHAT TO EXPECT */}
        <section style={{ background: "#eaf2fa" }}>
          <div className="summit-container py-16 lg:py-20">
            <div className="text-center">
              <div className="section-pill mb-4 inline-block">What to Expect</div>
              <h2
                className="font-serif mx-auto"
                style={{ color: "#1E1968", fontSize: "clamp(1.75rem, 3vw, 2.3rem)", fontWeight: 600, lineHeight: 1.15, maxWidth: 740 }}
              >
                Straightforward, Helpful, and Confidential
              </h2>
              <p className="mt-4 text-base mx-auto" style={{ color: "#3a4d70", maxWidth: 680, lineHeight: 1.6 }}>
                Our assessment is designed to be a simple, low-pressure way to organize your thoughts and get educational insights.
              </p>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {EXPECT.map(({ icon: Icon, title, copy }) => (
                <div key={title} className="flex gap-4">
                  <span
                    aria-hidden="true"
                    style={{
                      flex: "0 0 auto",
                      width: 48, height: 48,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      background: "#ffffff", color: "#1E1968", borderRadius: 999, border: "1px solid #cfe0f1",
                    }}
                  >
                    <Icon style={{ width: 22, height: 22 }} strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.05rem", fontWeight: 600, lineHeight: 1.3 }}>
                      {title}
                    </div>
                    <p className="mt-2 text-sm" style={{ color: "#17233D", lineHeight: 1.6 }}>{copy}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 flex justify-center">
              <Link to="/assessment" className="btn-primary" data-testid="hiw-start-cta">
                Take the Assessment <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

      </main>
      <MinimalFooter />
    </div>
  );
}
